import { useEffect, useMemo, useState } from 'react';
import {
  N4, inCheck4, legal4, onBoard4, xy4,
  type Kind4, type Move4, type Seat, type State4,
} from '@ic/engine';
import { settings, useStore } from '../lib/store';
import { PieceIcon } from './Pieces';

export const ICON4: Record<Kind4, 'king' | 'queen' | 'rook' | 'bishop' | 'knight' | 'pawn'> = { K: 'king', Q: 'queen', R: 'rook', B: 'bishop', N: 'knight', P: 'pawn' };

/** Screen column/row of a square with `seat` sitting at the bottom. */
export function place(sq: number, seat: Seat): [number, number] {
  const [x, y] = xy4(sq);
  const m = N4 - 1;
  switch (seat) {
    case 'w': return [x, m - y];
    case 'b': return [m - x, y];
    case 'g': return [m - y, m - x];
    case 'y': return [y, x];
  }
}

export function Board4({ state, seat, interactive, onMove, pick }: {
  state: State4;
  /** The player at the bottom of the screen. */
  seat: Seat;
  interactive: boolean;
  onMove?: (m: Move4) => void;
  /** Square picking (admin spawn). */
  pick?: { can: (sq: number) => boolean; onPick: (sq: number) => void } | null;
}) {
  const { boardTheme, pieceSet, coords } = useStore(settings);
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => setSel(null), [state.ply, state.turn, state.result]);
  const legal = useMemo(() => (interactive ? legal4(state) : []), [state, interactive]);
  const targets = sel == null ? [] : legal.filter((m) => m.from === sel);
  const checked = new Set(state.alive.filter((c) => inCheck4(state, c)));

  function click(sq: number) {
    if (pick) { if (pick.can(sq)) pick.onPick(sq); return; }
    if (!interactive) return;
    const m = targets.find((t) => t.to === sq);
    if (m) { onMove?.(m); setSel(null); return; }
    setSel(legal.some((x) => x.from === sq) && sel !== sq ? sq : null);
  }

  const cells = [];
  for (let sq = 0; sq < N4 * N4; sq++) {
    const [x, y] = xy4(sq);
    if (!onBoard4(x, y)) continue;
    const [col, row] = place(sq, seat);
    const p = state.board[sq];
    const light = (x + y) % 2 === 1;
    const cls = ['sq4', light ? 'light' : 'dark'];
    if (state.last && (state.last.from === sq || state.last.to === sq)) cls.push('last');
    if (sel === sq) cls.push('sel');
    if (p?.k === 'K' && p.c !== 'x' && checked.has(p.c)) cls.push('check');
    const target = targets.find((t) => t.to === sq);
    if (target) cls.push(p ? 'capture' : 'target');
    if (pick?.can(sq)) cls.push('pick-target');
    cells.push(
      <button key={sq} type="button" className={cls.join(' ')} style={{ gridColumn: col + 1, gridRow: row + 1 }}
        onClick={() => click(sq)} aria-label={`${'abcdefghijklmn'[x]}${y + 1}${p ? ` ${p.c} ${ICON4[p.k]}` : ''}`}>
        {p && <span className="piece4"><PieceIcon icon={ICON4[p.k]} color={p.c} set={pieceSet} /></span>}
        {coords && col === 0 && <span className="coord4 rank">{seat === 'w' || seat === 'b' ? y + 1 : 'abcdefghijklmn'[x]}</span>}
      </button>,
    );
  }
  return <div className={`board4 theme-${boardTheme}`} role="grid" aria-label="Four-player board">{cells}</div>;
}

